import { useEffect, useState } from 'react';
import './styles/Stepper.css';
import Layout from 'wrappers/layout';
import Step1 from './components/Step1/index';
import Step2 from './components/Step2/index';
import Step3 from './components/Step3/index';
import { usePhysicalDeliveryStore } from 'store/DeliveryStore';
import BuyCoinsBarsServices from './services/buy-coins-bars.service';
import Loader from 'shared/common/loader';
import dashboardService from 'services/dashboard.service';
import { dashboardStore } from 'store/DashboardStore';
import { userStore } from 'store/UserStore';
import OrderStatusPopup from './components/Step3/OrderStatusPopup';

const Stepper = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState<null | boolean>(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<{
    show: boolean;
    status: 'success' | 'failure';
    message: string;
  }>({ show: false, status: 'success', message: '' });

  const steps = ['Select', 'Add Address', 'Pay'];

  const [setSummary, setCustomerHolding] = dashboardStore((state: any) => [
    state.setSummary,
    state.setCustomerHolding,
  ]);

  const [customerId, user] = userStore((state: any) => [
    state.customerId,
    state.user,
    state.selectedAccount,
    state.customer,
  ]);

  const setSelectedProduct = usePhysicalDeliveryStore(
    (state) => state.setSelectedProduct
  );

  const getPassbookSummary = async () => {
    setLoading(true);
    try {
      const res: any = await dashboardService.getPassbookSummary(
        customerId,
        user?.userId
      );
      if (res.status === 200 && res.data.data) {
        setSummary(res.data.data.summary);
        setCustomerHolding(res.data.data.metal.customerHolding);
        return true;
      } else {
        return false;
      }
    } catch (e: any) {
      console.error(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getSingleProduct = async (itemCode: string) => {
    setLoading(true);
    try {
      const response = await BuyCoinsBarsServices.getSingleProduct(itemCode);
      if (response.status !== 200) throw new Error('Failed to fetch product');
      setSelectedProduct(response?.data[0]);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // const isDisabled = paymentStatus !== null;
  const isDisabled = popup.show || paymentStatus !== null;

  const nextStep = () => {
    if (currentStep < steps.length && !isDisabled) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1 && !isDisabled) {
      setCurrentStep(currentStep - 1);
      if (currentStep === steps.length) {
        setPaymentStatus(null);
      }
    }
  };

  const handlePaymentComplete = (status: boolean) => {
    setPaymentStatus(status); // keep stepper indicators updated

    setPopup({
      show: true,
      status: status ? 'success' : 'failure',
      message: status
        ? 'Your order has been placed successfully!'
        : 'Payment failed. Please try again.',
    });
  };

  const completePayment = () => setPaymentStatus(true);
  const failPayment = () => setPaymentStatus(false);

  useEffect(() => {
    const init = async () => {
      const itemCodeLs = localStorage.getItem('Product');
      if (itemCodeLs) {
        await getSingleProduct(itemCodeLs);
        setCurrentStep(2);
        localStorage.removeItem('Product');
      }
      await getPassbookSummary();
    };

    init();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  return (
    <Layout>
      {loading && <Loader />}
      <div className='stepper-container'>
        <div className='stepper-wrapper'>
          {steps.map((step, index) => (
            <div key={index} className='stepper-group'>
              <div
                className={`stepper-item ${currentStep > index + 1 ||
                  (index === 2 && paymentStatus !== null)
                  ? paymentStatus !== null && index === 2
                    ? paymentStatus
                      ? 'completed'
                      : 'failed'
                    : 'completed'
                  : currentStep === index + 1
                    ? 'active'
                    : ''
                  }`}
              >
                {index === 2 && paymentStatus !== null
                  ? paymentStatus
                    ? '✓'
                    : '✗'
                  : currentStep > index + 1
                    ? '✓'
                    : index + 1}
              </div>

              <span className='step-name'>{step}</span>

              {index < steps.length - 1 && (
                <div className='step-line-wrapper'>
                  <div
                    className={`step-line ${currentStep > index + 1 || paymentStatus !== null
                      ? 'active-line'
                      : ''
                      }`}
                  ></div>
                  <span
                    className={`arrow ${currentStep > index + 1 || paymentStatus !== null
                      ? 'active-arrow'
                      : ''
                      }`}
                  >
                    <svg
                      width='24px'
                      height='24px'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M10 7L15 12L10 17'
                        stroke='#000000'
                        strokeWidth='1'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      ></path>
                    </svg>
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className='step-content'>
          {currentStep === 1 && <Step1 nextStep={nextStep} />}
          {currentStep === 2 && (
            <Step2 nextStep={nextStep} prevStep={prevStep} />
          )}
          {currentStep === 3 && paymentStatus === null && (
            <Step3
              prevStep={prevStep}
              // onPaymentComplete={(status: boolean) => setPaymentStatus(status)}
              onPaymentComplete={handlePaymentComplete}
            />
          )}


        </div>

        {popup.show && (
          <OrderStatusPopup
            status={popup.status}
            show={popup.show}
            message={popup.message}
            onClose={() => {
              setPopup({ ...popup, show: false });
              if (popup.status === 'failure') {
                // Reset stepper to step 1
                setCurrentStep(1);
                setPaymentStatus(null);
              }
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default Stepper;
